/* eslint-disable max-len */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  Icon,
  Label,
  Popup,
  Table,
} from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import CurrencySymbol from './CurrencySymbol';
import LoanAmount from './LoanAmount';

import { TRANSLATION_FILES } from '../constants/translation';
import { isPricingPackageLocked, vnPackageLockMessage } from '../utils/vietnamPricing';
import { isVietnamCompany } from '../utils/vietnam';

class BookingPricingPackage extends Component {
  static propTypes = {
    availablePP: PropTypes.arrayOf(PropTypes.shape()),
    data: PropTypes.shape(),
    onCheckRecalculate: PropTypes.func,
    isEditing: PropTypes.bool,
    auth: PropTypes.shape({
      company: PropTypes.shape({ countryCode: PropTypes.string }),
      isSuperAdmin: PropTypes.bool,
      isAdminBasic: PropTypes.bool,
      isAdmin: PropTypes.bool,
      isDeveloperSupervisor: PropTypes.bool,
      isDeveloperSalesManager: PropTypes.bool,
      isDeveloperSalesAdmin: PropTypes.bool,
      isHeadquarters: PropTypes.bool,
    }),
    globalSettings: PropTypes.shape(),
    openModal: PropTypes.func,
    t: PropTypes.func,
  }
  getPackagePricingName = () => {
    const { data } = this.props;
    if (data && data.packagePricing) {
      const pp = data.packagePricing;
      return pp.isCustomPrice ? pp.customName : pp.name;
    }
    return '';
  }
  hasPackage = (data, notCustom = false) => {
    if (notCustom && data.packagePricing && data.packagePricing.projectPackagePricingID === '') return false;
    if (data.packagePricing && data.packagePricing.id !== '') return true;
    return false;
  }
  hasRecalculateAccess = () => {
    const { auth } = this.props;
    if (auth.isAdminBasic || auth.isSuperAdmin) return true;
    return false;
  }
  hasEditPackageAccess = () => {
    const { auth, data } = this.props;
    return (data.statusID === 'sold_spa_signed' &&
      (auth.isAdmin || auth.isAdminBasic || auth.isSuperAdmin)) ||
      auth.isDeveloperSupervisor ||
      auth.isDeveloperSalesManager ||
      auth.isDeveloperSalesAdmin ||
      auth.isHeadquarters;
  }
  hasAccessNettSellingPrice(project) {
    const { auth } = this.props;
    if (auth.isSuperAdmin || auth.isHeadquarters) {
      return true;
    }
    if (project && project.accessBULNettSellingPrice && auth.isBranchLeader) {
      return true;
    }
    if (project && project.accessStaffNettSellingPrice && auth && auth.companyBranch) {
      return true;
    }
    return false;
  }
  disablePricingEditAfterSold = data => data && data.statusID === 'sold_spa_signed' && data.project && !data.project.allowPricingEditAfterSold
  // CPI-977 requirements 7/11: in Vietnam the pricing package is HQ-only while Reserved and frozen from Booked.
  isVNPricingLocked = () => isPricingPackageLocked(this.props.auth, this.props.data && this.props.data.statusID)
  // CPI-977 (PO, 14 Sep): Vietnam shows only the header - package name, Edit Package and the
  // lock notice. The price breakdown and the formula editor stay hidden for now.
  isVNHeaderOnly = () => isVietnamCompany(this.props.auth)
  handleRecalculate = (e, data) => {
    if (this.props.onCheckRecalculate) {
      this.props.onCheckRecalculate(data.checked);
    }
  }
  renderCalculationType = (type, value, format, rounding) => {
    const { globalSettings, t } = this.props;
    const listPriceName = globalSettings && globalSettings.unitSellingPrice ? globalSettings.unitSellingPrice.toLowerCase() : t('Unit Selling Price').toLowerCase();
    const spaPriceName = globalSettings && globalSettings.sellingPrice ? globalSettings.sellingPrice.toLowerCase() : t('Selling Price').toLowerCase();
    let roundingType = '';
    if (rounding.includes('round')) {
      roundingType = 'round';
    }
    if (rounding.includes('round_up')) {
      roundingType = 'round up decimals';
    }
    if (rounding.includes('round_down')) {
      roundingType = 'round down decimals';
    }
    if (roundingType) {
      roundingType = ` (${t(roundingType)})`;
    }
    if (format === 'custom') {
      return t('Custom formula');
    }
    const text = format === 'percentage' ? `${value}%` : t('fixed amount');
    switch (type) {
      case 'deduct_from_list_price':
        return t('Deduct {{value}} from {{name}}', { value: text, name: `${listPriceName}${roundingType}` });
      case 'deduct_from_spa_price':
        return t('Deduct {{value}} from {{name}}', { value: text, name: `${spaPriceName}${roundingType}` });
      case 'deduct_from_sub_total':
        return t('Deduct {{value}} from {{name}}', { value: text, name: `${t('above\'s subtotal')}${roundingType}` });
      case 'add_from_list_price':
        return t('Add {{value}} from {{name}}', { value: text, name: `${listPriceName}${roundingType}` });
      case 'add_from_spa_price':
        return t('Add {{value}} from {{name}}', { value: text, name: `${spaPriceName}${roundingType}` });
      case 'add_from_sub_total':
        return t('Add {{value}} from {{name}}', { value: text, name: `${t('above\'s subtotal')}${roundingType}` });
      case 'free_item':
        return t('Free Item');
      case 'custom_formula':
        return t('Custom formula');
      default:
        return '';
    }
  }
  renderCalculationChangePrefix = (type) => {
    if (type.includes('deduct')) {
      return <Icon color="red" size="small" name="minus" />;
    }
    if (type.includes('add')) {
      return <Icon color="green" size="small" name="plus" />;
    }
    return '';
  }
  renderCalculationChangeClass = (type) => {
    if (type.includes('deduct')) {
      return 'text_deduct';
    }
    if (type.includes('add')) {
      return 'text_add';
    }
    return '';
  }
  renderDescription = (itemDesc) => {
    let iDesc = itemDesc;
    if (iDesc.includes('\n')) {
      iDesc = iDesc.split('\n').map((desc, idx) => <span key={idx}>{desc}<br /></span>);
    }
    return iDesc;
  }

  // The whole card is read-only under the Vietnam lock; the reason is said once, in the header.
  renderTextButton = (title, name, placement) => {
    const { data, t } = this.props;
    if (this.isVNPricingLocked()) {
      return null;
    }
    return (
      <Popup
        trigger={
          <Label
            as="button"
            attached={placement || undefined}
            size="small"
            content={t(title)}
            onClick={() => this.props.openModal && this.props.openModal(name, true)}
            className="textButton"
            disabled={this.disablePricingEditAfterSold(data)}
          />
        }
        disabled={!this.disablePricingEditAfterSold(data)}
        content={t('Pricing details are locked because the booking has been marked as Sold', { ns: TRANSLATION_FILES.translation })}
      />
    );
  };

  renderHeader = () => {
    const { data, availablePP, t } = this.props;
    return (
      <span>
        {
          this.hasPackage(data) ? (
            <span>
              Pricing Package:&nbsp;
              <span className="text_name italic">
                {this.getPackagePricingName()}
              </span>
            </span>
          ) : <span>{t('No Pricing Package added')}</span>
        }
        {
          (this.hasEditPackageAccess() && availablePP && availablePP.length > 0) &&
          this.renderTextButton(`${this.hasPackage(data) ? 'Edit' : 'Add'} Package`, 'showEditPackageModal')
        }
        {
          this.isVNPricingLocked() && (
            <span className="text_formula_small">
              &nbsp;<Icon name="lock" size="small" />
              {t(vnPackageLockMessage(this.props.auth, data && data.statusID))}
            </span>
          )
        }
      </span>
    );
  };

  renderPackagePricingCalculations = (type) => {
    const { data, t } = this.props;
    const projectCurrencies = data.project.projectCurrency || null;
    const calculations = data && data.packagePricing && data.packagePricing.calculations ? data.packagePricing.calculations : [];
    const category = type === 'spa' ? 'spa_price' : 'list_price';
    return (
      calculations.filter(c => c.category === category && c.isEnabled).map((calc, idx) => {
        const name = (calc.isAdhoc && calc.title === 'free_item') ? calc.formulaValue : calc.name;
        return (
          <Table.Row key={idx} className="row_formula_items">
            <Table.Cell className="text_formula_small">{calc.isSubTotal ? t('Sub total') : name}</Table.Cell>
            <Table.Cell className="text_description">{this.renderDescription(calc.description)}</Table.Cell>
            <Table.Cell className="text_description" textAlign="right">{this.renderCalculationType(calc.calculationType, calc.formulaValue, calc.format, calc.rounding)}</Table.Cell>
            <Table.Cell textAlign="right">
              {!calc.isSubTotal ? (
                <div>
                  <div className="div_inline_block">{this.renderCalculationChangePrefix(calc.calculationType)}</div>
                  <div className={`div_inline_block ${this.renderCalculationChangeClass(calc.calculationType)}`}>
                    <LoanAmount
                      precise={false}
                      projectCurrencies={projectCurrencies}
                      format="0,000,000.00"
                      amount={calc.valueChange}
                      prefix={<CurrencySymbol />}
                    />
                  </div>
                </div>
              ) : ''}
            </Table.Cell>
            <Table.Cell textAlign="right">
              <LoanAmount
                precise={false}
                projectCurrencies={projectCurrencies}
                format="0,000,000.00"
                amount={calc.newValue}
                prefix={<CurrencySymbol />}
              />
            </Table.Cell>
          </Table.Row>
        );
      })
    );
  }

  render() {
    const { data, isEditing, globalSettings, t } = this.props;
    const projectCurrencies = data.project.projectCurrency || null;
    const hasCalculations = data && data.packagePricing && data.packagePricing.calculations && data.packagePricing.calculations.length > 0;
    const hasSpaCalculations = hasCalculations && data.packagePricing.calculations.filter(c => c.category === 'spa_price' && c.isEnabled).length > 0;
    return (
      <div className="bookingPricingPackage">
        {
          !this.isVNHeaderOnly() &&
          this.hasPackage(data) && !data.packagePricing.isCustomPrice &&
          this.hasEditPackageAccess() && (
            this.renderTextButton('Customise formula(s)', 'showCustomiseFormulaModal', 'top right')
         )
        }
        <Table compact unstackable structured>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell colSpan={5}>
                {this.renderHeader()}
                {(isEditing && this.hasRecalculateAccess()) ?
                  <div className="mobile-hide tablet-hide div_recalculate">
                    <Checkbox
                      // eslint-disable-next-line jsx-a11y/label-has-for
                      label={<label>{t('Force recalculate pricing package from project unit')} <Icon name="warning sign" /></label>}
                      onChange={this.handleRecalculate}
                      disabled={this.disablePricingEditAfterSold(data) || this.isVNPricingLocked()}
                    />
                  </div>
                  : ''
                }
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          {!this.isVNHeaderOnly() && (
          <Table.Body>
            {this.hasPackage(data) ? (
              <Table.Row>
                <Table.Cell colSpan={5}>
                  <span className="text_description">
                    {data.packagePricing ? this.renderDescription(data.packagePricing.description) : ''}
                  </span>
                </Table.Cell>
              </Table.Row>
            ) : null}
            {this.hasPackage(data) ? (
              <Table.Row negative>
                <Table.Cell colSpan={5} />
              </Table.Row>
            ) : null}
            <Table.Row className="row_total">
              <Table.Cell colSpan={1} style={{ width: '20%' }}>
                <b>
                  {
                    globalSettings && globalSettings.unitSellingPrice
                      ? globalSettings.unitSellingPrice
                      : t('Unit Selling Price')
                  }
                </b>
              </Table.Cell>
              <Table.Cell colSpan={1} style={{ width: '25%' }} />
              <Table.Cell colSpan={1} style={{ width: '20%' }} />
              <Table.Cell colSpan={1} style={{ width: '13%' }} />
              <Table.Cell style={{ width: '13%' }} textAlign="right">
                <LoanAmount
                  projectCurrencies={projectCurrencies}
                  format="0,000,000.00"
                  amount={data.unitSellingPrice}
                  prefix={<CurrencySymbol />}
                />
              </Table.Cell>
            </Table.Row>
            <Table.Row negative>
              <Table.Cell colSpan={5} />
            </Table.Row>
            {hasCalculations ? this.renderPackagePricingCalculations('list') : null}
            {this.hasPackage(data, true) ? (
              <Table.Row negative>
                <Table.Cell colSpan={5} />
              </Table.Row>
            ) : null}
            <Table.Row className="row_total">
              <Table.Cell colSpan={2}>
                {this.hasPackage(data) ? `${t('Total')} ` : ''}
                {
                  globalSettings && globalSettings.sellingPrice
                    ? globalSettings.sellingPrice
                    : t('Selling Price')
                }
              </Table.Cell>
              <Table.Cell colSpan={2} />
              <Table.Cell colSpan={1} textAlign="right">
                {
                  ((data.packagePricing || data.spaAmount) ?
                    <LoanAmount
                      precise={false}
                      projectCurrencies={projectCurrencies}
                      format="0,000,000.00"
                      amount={(data.packagePricing && data.packagePricing.spaPrice) || data.spaAmount}
                      prefix={<CurrencySymbol />}
                    />
                    : '-')
                }
              </Table.Cell>
            </Table.Row>
            {
              hasSpaCalculations ? (
                <Table.Row negative>
                  <Table.Cell colSpan={5} />
                </Table.Row>
              ) : null
            }
            {hasCalculations ? this.renderPackagePricingCalculations('spa') : null}
            {
              hasSpaCalculations ? (
                <Table.Row negative>
                  <Table.Cell colSpan={5} />
                </Table.Row>
              ) : null
            }
            {
              this.hasAccessNettSellingPrice(data.project) ? (
                <Table.Row className="row_total">
                  <Table.Cell colSpan={2}>
                    {this.hasPackage(data) ? `${t('Total')} ` : ''}
                    {
                      globalSettings && globalSettings.nettSellingPrice
                        ? globalSettings.nettSellingPrice
                        : t('Nett Selling Price')
                    }
                  </Table.Cell>
                  <Table.Cell colSpan={2} />
                  <Table.Cell colSpan={1} textAlign="right">
                    {
                      (data.packagePricing || data.nettSellingPrice ?
                        <LoanAmount
                          precise={false}
                          projectCurrencies={projectCurrencies}
                          format="0,000,000.00"
                          amount={data.packagePricing.totalPrice || data.nettSellingPrice}
                          prefix={<CurrencySymbol />}
                        />
                        : '-')
                    }
                  </Table.Cell>
                </Table.Row>
              ) : ''
            }
          </Table.Body>
          )}
        </Table>
      </div>
    );
  }
}
export default withTranslation(TRANSLATION_FILES.screens)(BookingPricingPackage);



// WEBPACK FOOTER //
// ./src/components/BookingPricingPackage.jsx